import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  useTheme,
  InputAdornment,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AdminSidebar from "./AdminSidebar";

// Chart data
const chartData = [
  { name: "Jan", orderCount: 10, sales: 500 },
  { name: "Feb", orderCount: 20, sales: 700 },
  { name: "Mar", orderCount: 30, sales: 900 },
  { name: "Apr", orderCount: 40, sales: 1100 },
  { name: "May", orderCount: 50, sales: 1300 },
];

// Medicine data
const medicineList = [
  {
    id: 1,
    medicineName: "Paracetamol",
    price: "$5",
    medineType: "Tablet",
    description: "Pain relief and fever reducer",
    totalPiece: "100",
    expiryDate: "2025-12-31",
  },
  {
    id: 2,
    medicineName: "Ibuprofen",
    price: "$7",
    medineType: "Capsule",
    description: "Anti-inflammatory",
    totalPiece: "80",
    expiryDate: "2024-10-15",
  },
  {
    id: 3,
    medicineName: "Amoxicillin",
    price: "$12",
    medineType: "Tablet",
    description: "Antibiotic for infections",
    totalPiece: "50",
    expiryDate: "2026-03-20",
  },
];

const drawerWidth = 240;

const AdminDashboard = () => {
  const [chartType, setChartType] = useState("order");
  const [searchText, setSearchText] = useState("");

  const theme = useTheme();

  const toggleChart = () => {
    setChartType((prev) => (prev === "order" ? "sales" : "order"));
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredMedicines = medicineList.filter((med) =>
    med.medicineName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AdminSidebar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          ml: { sm: `${drawerWidth}px` },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Admin Dashboard
        </Typography>

        {/* Toggle Button */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Button
            variant="contained"
            onClick={toggleChart}
            sx={{
              background: chartType === "order" ? "#8884d8" : "#82ca9d",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              fontWeight: "bold",
              px: 4,
              py: 1,
              borderRadius: "30px",
              transition: "0.3s",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            Switch to {chartType === "order" ? "Sales" : "Orders"}
          </Button>
        </Box>

        {/* Chart */}
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 3,
            boxShadow:
              "0 8px 20px rgba(0,0,0,0.2), 0 12px 42px rgba(0,0,0,0.1)",
            background: "linear-gradient(135deg, #f0f0f0, #ffffff)",
            backdropFilter: "blur(10px)",
            mb: 5,
          }}
        >
          <Typography variant="h6" mb={2}>
            {chartType === "order" ? "Order Statistics" : "Sales Statistics"}
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <defs>
                <linearGradient id="colorLine" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={chartType === "order" ? "#8884d8" : "#82ca9d"}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={chartType === "order" ? "#8884d8" : "#82ca9d"}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey={chartType === "order" ? "orderCount" : "sales"}
                stroke={`url(#colorLine)`}
                strokeWidth={3}
                dot={{ r: 6, strokeWidth: 2, fill: "#fff" }}
                activeDot={{ r: 8 }}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Search Bar */}
        <Box mb={3} display="flex" justifyContent="center">
          <TextField
            variant="outlined"
            placeholder="Search medicine..."
            value={searchText}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              maxWidth: 500,
              transition: "all 0.4s ease",
              "&:focus-within": {
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              },
            }}
          />
        </Box>

        {/* Medicine Table */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Medicine Inventory
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Total Pieces</TableCell>
                  <TableCell>Expiry Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMedicines.map((med) => (
                  <TableRow key={med.id}>
                    <TableCell>{med.id}</TableCell>
                    <TableCell>{med.medicineName}</TableCell>
                    <TableCell>{med.price}</TableCell>
                    <TableCell>{med.medineType}</TableCell>
                    <TableCell>{med.description}</TableCell>
                    <TableCell>{med.totalPiece}</TableCell>
                    <TableCell>{med.expiryDate}</TableCell>
                  </TableRow>
                ))}
                {filteredMedicines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No medicine found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
